import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// prime react component
import { Toolbar } from "primereact/toolbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

// data table option
import {
  bookingStatusDropdownItems,
  convertDateFromTo,
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";

// farm table column
import { FarmerBookingListColumns } from "../../appconfig/DataTableColumns";

// api services
import { getAllDetails } from "../../service/ProductService";

// api constant
import { GET_BOOKING_LIST, GET_SERVICE_LIST } from "../../config/ApiConstant";
import { Badge } from "primereact/badge";
import {
  amountTemplate,
  globalFieldTemplate,
} from "../../appconfig/TemplateSetting";
import { FilterMatchMode } from "primereact/api";
import {
  getBookingStatusSeverity,
  globalNameFilterTemplate,
} from "../../appconfig/AppHelper";
import { Dropdown } from "primereact/dropdown";

const FarmerBookingList = () => {
  document.title = "Farmer Booking List | Agriown";

  // Get User ID From URL
  const { id }: any = useParams();
  const farmer_id = atob(id);

  // navigation
  const navigate = useNavigate();

  // /Set Toast/ Filter Properties
  const toast = useRef<Toast>(null);
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [statusFilterValue, setStatusFilterValue] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [serviceFilterValue, setServiceFilterValue] = useState<any>(null);

  //Loading/Page Loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [farmerBookingList, setFarmerBookingList] = useState<any>([]);

  // datatable filter value sets
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    farm_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    operator_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    service_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    crop_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    total_amount: { value: null, matchMode: FilterMatchMode.CONTAINS },
    booking_status: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // Get Farmer Booking List on page load
  useEffect(() => {
    getFarmerBookingList();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [farmer_id]);

  // get services list for filter
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getAllDetails(GET_SERVICE_LIST);
        if (response) {
          setServices(response);
          setPageLoad(true);
        } else {
          setServices([]);
          setPageLoad(false);
        }
      } catch (error: any) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.response?.data?.message,
        });
        setPageLoad(false);
      }
    };
    fetchServices();
  }, []);

  // left toolbar
  const leftToolbarTemplate = () => {
    return (
      <span className="mr-2 md:mr-3 search-bar">
        <i className="pi pi-search" />
        <InputText
          type="search"
          className="search-input"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.currentTarget.value)}
          placeholder="Search..."
        />
      </span>
    );
  };

  //Clear Filters
  const rightToolbarTemplate = () => {
    return (
      <>
        <Button
          icon="pi pi-filter-slash"
          label="Clear"
          onClick={() => clearAll()}
          className="md:mr-2 p-button-outlined p-button-primary"
        ></Button>
      </>
    );
  };

  //Clear All Filter
  const clearAll = () => {
    setFilters({ ...emptyFilters });
    setGlobalFilter("");
    setServiceFilterValue(null);
    setStatusFilterValue(null);
  };

  // api call for get farmer booking list
  const getFarmerBookingList = async () => {
    try {
      const response = await getAllDetails(
        `${GET_BOOKING_LIST}?farmer_id=${farmer_id}`
      );

      if (response) {
        setFarmerBookingList(response);
        setPageLoad(true);
      } else {
        setFarmerBookingList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // Booking Status Template
  const bookingStatusTemplate = (rowData: any) => {
    return (
      <Badge
        value={rowData.booking_status}
        severity={getBookingStatusSeverity(rowData.booking_status)}
      />
    );
  };

  // Service Filter Dropdown
  const filterByService = (options: any) => {
    return (
      <Dropdown
        filter
        value={serviceFilterValue}
        options={services}
        onChange={(e) => {
          setServiceFilterValue(e.value);
          options.filterCallback(e.value ? e.value : null, options.index);
        }}
        optionLabel="service_name"
        optionValue="service_name"
        placeholder="Select Service"
        className="order-drp"
        showClear
      />
    );
  };

  // Status Filter Dropdown
  const filterByStatus = (options: any) => {
    return (
      <Dropdown
        filter
        value={statusFilterValue}
        options={bookingStatusDropdownItems}
        onChange={(e) => {
          setStatusFilterValue(e.value);
          options.filterCallback(e.value ? e.value : null, options.index);
        }}
        optionLabel="name"
        optionValue="code"
        placeholder="Select Status"
        className="order-drp"
        showClear
      />
    );
  };

  // action Template
  const actionBodyTemplate = (rowData: any) => (
    <div className="actions">
      <Button
        icon="pi pi-eye"
        className="mr-2 p-button-secondary"
        tooltip="View Detail"
        tooltipOptions={{ position: "top" }}
        onClick={() => navigate(`/booking/detail/${btoa(rowData.id)}`)}
      />
    </div>
  );

  // transaction amount template
  // transaction amount template
  const transactionAmountTemplate = (rowData: any) => {
    const total = rowData?.total_amount ?? 0;

    const paid =
      rowData?.payment_data?.reduce(
        (acc: number, p: any) => acc + (p.amount_paid || 0),
        0
      ) ?? 0;

    const remaining = Math.max(total - paid, 0);
    return (
      <div style={{ lineHeight: "1.4" }}>
        <div className="flex align-items-center gap-1 justify-content-center">
          <span>Paid Amount:</span>{" "}
          {amountTemplate({ paid_amount: paid }, { field: "paid_amount" })}
        </div>
        <div className="flex align-items-center gap-1 justify-content-center">
          <span>Remaining Amount:</span>{" "}
          {amountTemplate(
            { remaining_amount: remaining },
            { field: "remaining_amount" }
          )}
        </div>
      </div>
    );
  };

  // total farm template
  const totalFarmTemplate = (rowData: any) => {
    return (
      <span>
        {!window.cn(rowData?.booking_farm_data)
          ? rowData?.booking_farm_data.length
          : "-"}
      </span>
    );
  };

  return (
    <>
      <Toast ref={toast} />

      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <Toolbar
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            ></Toolbar>

            {pageLoad ? (
              <DataTable
                value={farmerBookingList}
                showGridlines
                stripedRows
                globalFilter={globalFilter || null}
                rows={defaultRowOptions}
                paginator={farmerBookingList.length >= 10}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatable-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries}
                filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                onFilter={(e: any) => setFilters(e.filters)}
                emptyMessage="No Data Found"
                removableSort
              >
                {FarmerBookingListColumns.map((col, i) => {
                  if (col.field === "action") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={actionBodyTemplate}
                      />
                    );
                  } else if (col.field === "booking_status") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        filter
                        filterElement={filterByStatus}
                        body={bookingStatusTemplate}
                      />
                    );
                  } else if (col.field === "service_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        body={globalFieldTemplate}
                        header={col.header}
                        sortable
                        filter
                        filterElement={filterByService}
                      />
                    );
                  } else if (col.field === "crop_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        sortable
                        filter
                        filterElement={globalNameFilterTemplate}
                      />
                    );
                  } else if (col.field === "transaction") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        body={transactionAmountTemplate}
                        header={col.header}
                      />
                    );
                  } else if (col.field === "total_amount") {
                    return (
                      <Column
                        body={amountTemplate}
                        key={col.field}
                        field={col.field}
                        header={col.header}
                      />
                    );
                  } else if (col.field === "total_farm") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        body={totalFarmTemplate}
                        header={col.header}
                      />
                    );
                  } else if (col.field === "booking_date") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={(rowData) =>
                          rowData.booking_date
                            ? convertDateFromTo(rowData.booking_date)
                            : "-"
                        }
                      />
                    );
                  } else if (col.field === "operator_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={globalFieldTemplate}
                        sortable
                        filter
                        filterElement={globalNameFilterTemplate}
                      />
                    );
                  } else {
                    return (
                      <Column
                        key={col.field}
                        sortable
                        field={col.field}
                        header={col.header}
                      />
                    );
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {FarmerBookingListColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FarmerBookingList;
